import { configureStore, combineReducers } from "@reduxjs/toolkit";
import type { PreloadedState } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import type { Persistor } from "redux-persist";
import storageSession from "redux-persist/lib/storage/session";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { createFilter } from "redux-persist-transform-filter";

import EditorReducer from "./redux/EditorSlice";
import InstructionsReducer from "./redux/InstructionsSlice";
import SchoolReducer from "./redux/SchoolSlice";
import ClassReducer from "./redux/ClassSlice";
import LessonReducer from "./redux/LessonSlice";
import UserJobReducer from "./redux/UserJobSlice";
import ClassImporterReducer from "./redux/ClassImporterSlice";
import FeaturesReducer from "./redux/FeaturesSlice";
import ProfileAuthStatusReducer from "./redux/ProfileAuthStatusReducer";

const saveUserLoginSubsetFilter = [
  createFilter("school", ["id", "autofillSchoolCode"]),
  createFilter("class", ["id"]),
  createFilter("lesson", ["id"]),
  createFilter("userJob", ["latestJobInfo"]),
  createFilter("classImporter", ["selectedCourse"]),
];

const persistConfig = {
  key: "root",
  storage: storageSession,
  whitelist: ["school", "class", "lesson", "userJob", "classImporter"],
  stateReconciler: autoMergeLevel2,
  transforms: [...saveUserLoginSubsetFilter],
};

const rootReducer = combineReducers({
  editor: EditorReducer,
  instructions: InstructionsReducer,
  school: SchoolReducer,
  class: ClassReducer,
  lesson: LessonReducer,
  userJob: UserJobReducer,
  classImporter: ClassImporterReducer,
  features: FeaturesReducer,
  profileAuthStatus: ProfileAuthStatusReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

const persistedReducer = persistReducer(persistConfig, rootReducer);

let persistor: Persistor | undefined;

export const purgePersistedStore = async (): Promise<void> => {
  if (persistor) {
    await persistor.purge();
  }
};

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  const store = configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            "redux-oidc/USER_FOUND",
            "redux-odic/SILENT_RENEW_ERROR",
            "persist/PERSIST",
            "persist/REHYDRATE",
            "persist/PURGE",
          ],
        },
      }),
  });
  persistor = persistStore(store);

  return { store, persistor };
};

export type AppDispatch = ReturnType<typeof setupStore>["store"]["dispatch"];
